import type {
  EventsSortColumn,
  EventsSortDir,
  FilterState,
  InteractionSessionCohort,
  TabId,
} from "../types.js";
import {
  type ChartTableSelection,
  chartTablesParamValue,
  parseChartTablesParam,
} from "./chartTables.js";
import {
  endOfDayDatetimeLocal,
  normalizeFilterDateTime,
  nowDatetimeLocal,
  prevFourCompleteWeeksRange,
  startOfDayDatetimeLocal,
} from "./filterDates.js";
import {
  loadStoredReportingTimezone,
  REPORTING_TIMEZONE_OPTIONS,
  REPORTING_TZ_BROWSER_DEFAULT,
} from "./reportingTimezone.js";
import { appendPassthroughUrlParams } from "./urlPassthrough.js";

const DAY_MS = 86400000;

export const TAB_IDS: TabId[] = [
  "overview",
  "health",
  "acquisition",
  "engagement",
  "retention",
  "sessions",
  "events",
  "presence",
];

export const defaultFilters: FilterState = {
  fleetId: "all",
  venueId: "all",
  deviceId: "all",
  gameUrl: "all",
  includeNonPublic: false,
  timeframe: "7d",
  from: startOfDayDatetimeLocal(Date.now() - 7 * DAY_MS),
  to: nowDatetimeLocal(),
};

export interface DashboardUrlState {
  tab: TabId;
  filters: FilterState;
  eventsOffset: number;
  eventsPageSize: number;
  eventsEventName: string;
  eventsPropertiesSearch: string;
  eventsPropertiesSearch2: string;
  eventsClientErrorBucket: "" | "warning" | "error";
  eventsInteractionSessionId: string;
  eventsInteractionSessionCohort: InteractionSessionCohort | "";
  sessionsListOffset: number;
  sessionsListLimit: number;
  eventsShowTemperature: boolean;
  eventsShowUptimePulse: boolean;
  eventsHideAttractBasics: boolean;
  eventsDedupePopupTapMissed: boolean;
  /** Inclusive °C lower bound for Raw Events heat-bucket filter (`events_temp_min`). */
  eventsTempCelsiusMin: number | undefined;
  /** Exclusive °C upper bound (`events_temp_max_excl`). */
  eventsTempCelsiusMaxExclusive: number | undefined;
  eventsSort: EventsSortColumn;
  eventsSortDir: EventsSortDir;
  sessionsLongOnly: boolean;
  sessionsPaidOnly: boolean;
  reportingTimezoneOption: string;
  /** Shared daily-chart table checklist. Omitted from the URL when `"all"`. */
  chartTables: ChartTableSelection;
  /** Shared Full weeks grouping. Omitted from the URL when false. */
  chartFullWeeks: boolean;
}

export function parseDashboardUrlState(search = window.location.search): DashboardUrlState {
  const params = new URLSearchParams(search);
  const tabParam = params.get("tab");
  const tab: TabId = TAB_IDS.includes(tabParam as TabId) ? (tabParam as TabId) : "overview";
  const timeframeParam = params.get("timeframe");
  const timeframe =
    timeframeParam === "today" ||
    timeframeParam === "yesterday" ||
    timeframeParam === "7d" ||
    timeframeParam === "30d" ||
    timeframeParam === "prev4w" ||
    timeframeParam === "custom"
      ? timeframeParam
      : defaultFilters.timeframe;

  let presetFrom: string;
  let presetTo: string;
  if (timeframe === "today") {
    presetFrom = startOfDayDatetimeLocal(Date.now());
    presetTo = nowDatetimeLocal();
  } else if (timeframe === "yesterday") {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    presetFrom = startOfDayDatetimeLocal(d.getTime());
    presetTo = endOfDayDatetimeLocal(d.getTime());
  } else if (timeframe === "7d") {
    presetFrom = startOfDayDatetimeLocal(Date.now() - 7 * DAY_MS);
    presetTo = nowDatetimeLocal();
  } else if (timeframe === "30d") {
    presetFrom = startOfDayDatetimeLocal(Date.now() - 30 * DAY_MS);
    presetTo = nowDatetimeLocal();
  } else if (timeframe === "prev4w") {
    const range = prevFourCompleteWeeksRange();
    presetFrom = range.from;
    presetTo = range.to;
  } else {
    presetFrom = defaultFilters.from;
    presetTo = defaultFilters.to;
  }

  const filters: FilterState = {
    fleetId: params.get("fleet_id") ?? defaultFilters.fleetId,
    venueId: params.get("venue_id") ?? defaultFilters.venueId,
    deviceId: params.get("device_id") ?? defaultFilters.deviceId,
    gameUrl: params.get("game_url") ?? defaultFilters.gameUrl,
    includeNonPublic: params.get("include_non_public") === "1",
    timeframe,
    from:
      timeframe === "custom"
        ? normalizeFilterDateTime(params.get("from"), defaultFilters.from, "from")
        : presetFrom,
    to:
      timeframe === "custom"
        ? normalizeFilterDateTime(params.get("to"), defaultFilters.to, "to")
        : presetTo,
  };

  const eventsOffsetRaw = Number.parseInt(params.get("events_offset") ?? "0", 10);
  const eventsOffset = Number.isFinite(eventsOffsetRaw) && eventsOffsetRaw >= 0 ? eventsOffsetRaw : 0;
  const eventsEventName = params.get("event_name") ?? "all";
  const eventsPropertiesSearch = (params.get("events_props") ?? "").trim().slice(0, 500);
  const eventsPropertiesSearch2 = (params.get("events_props_2") ?? "").trim().slice(0, 500);
  const cc = params.get("events_cc");
  const eventsClientErrorBucket: "" | "warning" | "error" =
    cc === "warning" || cc === "error" ? cc : "";
  const eventsInteractionSessionId = (params.get("events_isid") ?? "").trim().slice(0, 128);
  const isc = params.get("events_iscohort");
  const eventsInteractionSessionCohort: InteractionSessionCohort | "" =
    isc === "<1m" || isc === "1-5m" || isc === "5-30m" || isc === "30m+"
      ? isc
      : "";

  const sessionsListOffsetRaw = Number.parseInt(params.get("sessions_offset") ?? "0", 10);
  const sessionsListOffset =
    Number.isFinite(sessionsListOffsetRaw) && sessionsListOffsetRaw >= 0
      ? sessionsListOffsetRaw
      : 0;

  const SESSION_PAGE_SIZES = [25, 50, 100, 200] as const;
  const sessionsLimitRaw = Number.parseInt(params.get("sessions_limit") ?? "50", 10);
  const sessionsListLimit = SESSION_PAGE_SIZES.includes(
    sessionsLimitRaw as (typeof SESSION_PAGE_SIZES)[number],
  )
    ? sessionsLimitRaw
    : 50;

  const eventsLimitRaw = Number.parseInt(params.get("events_limit") ?? "50", 10);
  const EVENT_PAGE_SIZES = [25, 50, 100, 200] as const;
  const eventsPageSize = EVENT_PAGE_SIZES.includes(eventsLimitRaw as (typeof EVENT_PAGE_SIZES)[number])
    ? eventsLimitRaw
    : 50;

  /** Default: hide temperature / pulse (unchecked). `events_inc_*=1` shows them. */
  const eventsShowTemperature = params.get("events_inc_temp") === "1";
  const eventsShowUptimePulse = params.get("events_inc_pulse") === "1";
  /** Default: hide attract basics (unchecked). `events_hide_attract=0` shows them. */
  const eventsHideAttractBasics = params.get("events_hide_attract") !== "0";
  /** Default: collapse popup_tap_missed bursts (checked). `events_dedupe_tap_missed=0` shows every row. */
  const eventsDedupePopupTapMissed = params.get("events_dedupe_tap_missed") !== "0";

  const parseTempBound = (raw: string | null): number | undefined => {
    if (raw == null || raw.trim() === "") return undefined;
    const n = Number(raw);
    return Number.isFinite(n) ? n : undefined;
  };
  const eventsTempCelsiusMin = parseTempBound(params.get("events_temp_min"));
  const eventsTempCelsiusMaxExclusive = parseTempBound(params.get("events_temp_max_excl"));

  const EVENTS_SORT_COLUMNS: EventsSortColumn[] = [
    "client_timestamp",
    "event_name",
    "device_id",
    "properties",
  ];
  const sortParam = params.get("events_sort");
  const eventsSort: EventsSortColumn = EVENTS_SORT_COLUMNS.includes(sortParam as EventsSortColumn)
    ? (sortParam as EventsSortColumn)
    : "client_timestamp";
  const eventsSortDir: EventsSortDir =
    eventsInteractionSessionId && !params.has("events_sort_dir")
      ? "asc"
      : params.get("events_sort_dir") === "asc"
        ? "asc"
        : "desc";

  const sessionsLongOnly = params.get("sessions_long") === "1";
  const sessionsPaidOnly = params.get("sessions_paid_only") === "1";

  const reportingTzParam = params.get("reporting_tz");
  let reportingTimezoneOption = loadStoredReportingTimezone();
  if (
    reportingTzParam &&
    (REPORTING_TIMEZONE_OPTIONS.includes(reportingTzParam as (typeof REPORTING_TIMEZONE_OPTIONS)[number]) ||
      reportingTzParam === REPORTING_TZ_BROWSER_DEFAULT)
  ) {
    reportingTimezoneOption = reportingTzParam;
  } else if (
    reportingTzParam &&
    /^[A-Za-z0-9_+/-]+$/.test(reportingTzParam) &&
    reportingTzParam.length <= 64
  ) {
    reportingTimezoneOption = reportingTzParam;
  }

  return {
    tab,
    filters,
    eventsOffset,
    eventsPageSize,
    eventsEventName,
    eventsPropertiesSearch,
    eventsPropertiesSearch2,
    eventsClientErrorBucket,
    eventsInteractionSessionId,
    eventsInteractionSessionCohort,
    sessionsListOffset,
    sessionsListLimit,
    eventsShowTemperature,
    eventsShowUptimePulse,
    eventsHideAttractBasics,
    eventsDedupePopupTapMissed,
    eventsTempCelsiusMin,
    eventsTempCelsiusMaxExclusive,
    eventsSort,
    eventsSortDir,
    sessionsLongOnly,
    sessionsPaidOnly,
    reportingTimezoneOption,
    chartTables: parseChartTablesParam(params.get("tables")),
    chartFullWeeks: params.get("weeks") === "1",
  };
}

export function buildDashboardUrl(state: DashboardUrlState): string {
  const params = new URLSearchParams();
  params.set("tab", state.tab);
  params.set("timeframe", state.filters.timeframe);

  if (state.filters.fleetId && state.filters.fleetId !== "all") {
    params.set("fleet_id", state.filters.fleetId);
  }
  if (state.filters.venueId && state.filters.venueId !== "all") {
    params.set("venue_id", state.filters.venueId);
  }
  if (state.filters.deviceId && state.filters.deviceId !== "all") {
    params.set("device_id", state.filters.deviceId);
  }
  if (state.filters.gameUrl && state.filters.gameUrl !== "all") {
    params.set("game_url", state.filters.gameUrl);
  }
  if (state.filters.includeNonPublic) {
    params.set("include_non_public", "1");
  }
  if (state.filters.timeframe === "custom") {
    if (state.filters.from) params.set("from", state.filters.from);
    if (state.filters.to) params.set("to", state.filters.to);
  }

  if (state.tab === "events") {
    if (state.eventsEventName && state.eventsEventName !== "all") {
      params.set("event_name", state.eventsEventName);
    }
    if (state.eventsOffset > 0) params.set("events_offset", String(state.eventsOffset));
    if (state.eventsPageSize !== 50) params.set("events_limit", String(state.eventsPageSize));
    if (state.eventsPropertiesSearch) params.set("events_props", state.eventsPropertiesSearch);
    if (state.eventsPropertiesSearch2) params.set("events_props_2", state.eventsPropertiesSearch2);
    if (state.eventsClientErrorBucket === "warning" || state.eventsClientErrorBucket === "error") {
      params.set("events_cc", state.eventsClientErrorBucket);
    }
    if (state.eventsInteractionSessionId) params.set("events_isid", state.eventsInteractionSessionId);
    if (state.eventsInteractionSessionCohort) {
      params.set("events_iscohort", state.eventsInteractionSessionCohort);
    }
    if (state.eventsShowTemperature) params.set("events_inc_temp", "1");
    if (state.eventsShowUptimePulse) params.set("events_inc_pulse", "1");
    if (!state.eventsHideAttractBasics) params.set("events_hide_attract", "0");
    if (!state.eventsDedupePopupTapMissed) params.set("events_dedupe_tap_missed", "0");
    if (state.eventsTempCelsiusMin != null) {
      params.set("events_temp_min", String(state.eventsTempCelsiusMin));
    }
    if (state.eventsTempCelsiusMaxExclusive != null) {
      params.set("events_temp_max_excl", String(state.eventsTempCelsiusMaxExclusive));
    }
    if (state.eventsSort !== "client_timestamp") params.set("events_sort", state.eventsSort);
    if (state.eventsSortDir !== "desc") params.set("events_sort_dir", state.eventsSortDir);
  }

  if (state.tab === "sessions") {
    if (state.sessionsListOffset > 0) params.set("sessions_offset", String(state.sessionsListOffset));
    if (state.sessionsListLimit !== 50) params.set("sessions_limit", String(state.sessionsListLimit));
    if (state.sessionsLongOnly) params.set("sessions_long", "1");
    if (state.sessionsPaidOnly) params.set("sessions_paid_only", "1");
  }

  if (state.reportingTimezoneOption !== REPORTING_TZ_BROWSER_DEFAULT) {
    params.set("reporting_tz", state.reportingTimezoneOption);
  }

  const tables = chartTablesParamValue(state.chartTables);
  if (tables) params.set("tables", tables);
  if (state.chartFullWeeks) params.set("weeks", "1");

  appendPassthroughUrlParams(params);

  const qs = params.toString();
  return qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
}
